import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { ChevronRight, Cpu, Zap, Activity, Settings, Lightbulb, Star, ArrowRight } from 'lucide-react';
import { mockServices, mockTestimonials, mockGalleryItems } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const iconMap = {
  Cpu: Cpu,
  Zap: Zap,
  Activity: Activity,
  Settings: Settings,
  Lightbulb: Lightbulb
};

// Hero Slideshow Component
const HeroSlideshow = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/oi9pj01m_IMG_0061.jpg', position: 'center 60%' },
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/5hkzaktk_IMG_7070.jpeg', position: 'center 50%' },
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/7vfwnk4l_59FFED06-30BC-4A8B-B7A4-9978AB40D694.jpeg', position: 'center 50%' },
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/vj9sfned_IMG_7381.jpeg', position: 'center 85%' }, // Evo with city - moved even lower to show cars fully
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/aa4s0qxa_IMG_1076.jpeg', position: 'center 75%' }, // White E63 night city - moved lower
    { url: 'https://customer-assets.emergentagent.com/job_9a561175-5bf3-4a7e-a383-8fff68bc2d5c/artifacts/zgp9t1q9_IMG_2035.jpg', position: 'center 60%' }, // Black C63 S in carpark at night
    { url: 'https://customer-assets.emergentagent.com/job_car-tune-experts/artifacts/iuc1s3ha_IMG_3268.jpeg', position: 'center 85%' } // White Audi and black car at Auckland waterfront night - moved lower
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <div className="hero-slideshow">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
          style={{ 
            backgroundImage: `url(${slide.url})`,
            backgroundPosition: slide.position
          }}
        />
      ))}
    </div>
  );
};

const Landing = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    rego: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/quotes`, formData);
      toast.success("Quote Request Received!", {
        description: "We'll contact you within 24 hours to discuss your tuning needs.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicle: '',
        rego: '',
        message: ''
      });
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error("Submission Failed", {
        description: "Please try again or contact us directly at Torqueautohaus1@gmail.com",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-container">
      {/* Navigation Header */}
      <header className="dark-header">
        <div className="custom-logo" data-testid="site-logo">
          <img src="/assets/logo-black.png" alt="Torque Autohaus" className="logo-image" />
        </div>
        <nav className="dark-nav">
          <a href="#performance-tuning" className="dark-nav-link">Performance Tuning</a>
          <a href="#services" className="dark-nav-link">Services</a>
          <a href="#gallery" className="dark-nav-link">Gallery</a>
          <a href="#testimonials" className="dark-nav-link">Testimonials</a>
          <a href="#contact" className="dark-nav-link">Contact</a>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <HeroSlideshow />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">
            Precision Engineering.<br />
            Maximum Performance.
          </h1>
          <p className="hero-subtitle">
            Elite vehicle tuning and performance optimization for enthusiasts who demand the absolute best.
          </p>
          <div className="hero-cta-group">
            <Button className="btn-primary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              Get Your Quote
              <ChevronRight className="w-5 h-5" />
            </Button>
            <Button className="btn-secondary" onClick={() => document.getElementById('performance-tuning').scrollIntoView({ behavior: 'smooth' })}>
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Performance Tuning Section */}
      <section id="performance-tuning" className="performance-tuning-section">
        <div className="tuning-container">
          <div className="tuning-content">
            <h2 className="section-title" style={{ textAlign: 'center' }}>Unlock Your Vehicle's True Potential</h2>
            <p className="tuning-intro">
              Your vehicle left the factory with untapped power, efficiency, and performance deliberately limited by conservative engineering. At Torque Autohaus, we specialize in professional ECU remapping and performance optimization that safely unleashes what your engine was truly capable of achieving.
            </p>
            
            <div className="tuning-benefits">
              <div className="benefit-item">
                <div className="benefit-number">+20-40%</div>
                <h3 className="benefit-title">Power Gains</h3>
                <p className="benefit-description">Experience dramatic increases in horsepower and torque with custom ECU calibration tailored to your vehicle's specific configuration.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-number">+15-25%</div>
                <h3 className="benefit-title">Fuel Efficiency</h3>
                <p className="benefit-description">Optimized fuel mapping delivers better throttle response while potentially improving your fuel economy during normal driving conditions.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-number">100%</div>
                <h3 className="benefit-title">Reversible</h3>
                <p className="benefit-description">All our tuning solutions are completely reversible. Return to factory settings anytime with no permanent modifications to your vehicle.</p>
              </div>
            </div>
            
            <div className="tuning-packages">
              <h3 className="tuning-subtitle">Our Performance Packages</h3>
              <div className="packages-grid">
                <div className="package-card">
                  <h4 className="package-title">Stage 1</h4>
                  <p className="package-subtitle">ECU Remap Only</p>
                  <ul className="package-features">
                    <li>Software optimization only</li>
                    <li>+15-25% power increase</li>
                    <li>+20-30% torque increase</li>
                    <li>Improved throttle response</li>
                    <li>Better fuel economy</li>
                    <li>No hardware modifications required</li>
                  </ul>
                  <p className="package-ideal">Ideal for daily drivers seeking instant performance gains without mechanical changes.</p>
                </div>
                
                <div className="package-card package-popular">
                  <div className="popular-badge">Most Popular</div>
                  <h4 className="package-title">Stage 2</h4>
                  <p className="package-subtitle">ECU + Performance Hardware</p>
                  <ul className="package-features">
                    <li>Stage 2</li>
                    <li>+25-35% power increase</li>
                    <li>+30-40% torque increase</li>
                    <li>Performance downpipe</li>
                    <li>High-flow air intake</li>
                    <li>Upgraded intercooler (turbo vehicles)</li>
                  </ul>
                  <p className="package-ideal">Perfect balance of performance and reliability for enthusiasts wanting serious power gains.</p>
                </div>
                
                <div className="package-card">
                  <h4 className="package-title">Stage 3</h4>
                  <p className="package-subtitle">Full Performance Build</p>
                  <ul className="package-features">
                    <li>Stage 3 components</li>
                    <li>+40-60% power increase</li>
                    <li>+50-70% torque increase</li>
                    <li>Turbo/supercharger upgrades</li>
                    <li>Upgraded fuel system</li>
                    <li>Performance clutch & transmission work</li>
                  </ul>
                  <p className="package-ideal">Maximum performance for track-focused builds and serious motorsport applications.</p>
                </div>
              </div>
            </div>
            
            <div className="tuning-description">
              <h3 className="tuning-subtitle">Why Choose Professional ECU Tuning?</h3>
              <p className="tuning-text">
                Modern vehicles are controlled by sophisticated engine management systems that regulate power delivery, fuel consumption, and emissions. Manufacturers program these systems conservatively to account for varying fuel quality, driver skill levels, and maintenance schedules across global markets.
              </p>
              <p className="tuning-text">
                Our expert technicians use state-of-the-art diagnostic equipment and proprietary tuning software to safely recalibrate your ECU parameters. We optimize ignition timing, boost pressure, fuel delivery, and torque limiters to extract maximum performance while maintaining complete reliability and drivability.
              </p>
              <p className="tuning-text">
                Every tune is custom-tailored to your specific vehicle, driving style, and performance goals. Whether you're seeking aggressive track performance or refined daily driving dynamics, we deliver precision engineering that transforms your driving experience.
              </p>
            </div>
            
            <div className="tuning-cta">
              <Button className="btn-primary" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
                Request Your Custom Tune
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-header">
          <h2 className="section-title">Our Services</h2>
          <p className="section-description">Professional automotive services and performance solutions</p>
        </div>
        <div className="services-list">
          <div className="service-item">
            <h3 className="service-item-title">ECU & TCU Tuning</h3>
            <p className="service-item-description">Custom engine and transmission control unit remapping for maximum performance and efficiency. Tailored to your driving style.</p>
          </div>
          <div className="service-item">
            <h3 className="service-item-title">Suspension Upgrades</h3>
            <p className="service-item-description">Performance suspension systems, coilovers, and alignment services for enhanced handling, comfort, and driving dynamics.</p>
          </div>
          <div className="service-item">
            <h3 className="service-item-title">Custom Interior Lighting</h3>
            <p className="service-item-description">Premium ambient lighting systems and starlight headliner installations to transform your vehicle's interior atmosphere.</p>
          </div>
          <div className="service-item">
            <h3 className="service-item-title">Diagnostic Services</h3>
            <p className="service-item-description">Advanced diagnostic tools to identify and resolve performance issues, fault codes, and system problems accurately.</p>
          </div>
          <div className="service-item">
            <h3 className="service-item-title">DPF Delete</h3>
            <p className="service-item-description">Diesel particulate filter removal and ECU reprogramming for improved performance, fuel economy, and reduced maintenance.</p>
          </div>
          <div className="service-item">
            <h3 className="service-item-title">Oil Services & Maintenance</h3>
            <p className="service-item-description">Complete oil change services, filter replacements, and routine maintenance to keep your vehicle running at peak performance.</p>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="section-header">
          <h2 className="section-title">Featured Builds</h2>
          <p className="section-description">Real results from real clients</p>
        </div>
        <div className="gallery-grid">
          {mockGalleryItems.map((item) => (
            <div key={item.id} className="gallery-item">
              <img src={item.image} alt={item.title} className="gallery-image" />
              <div className="gallery-overlay">
                <h3 className="gallery-title">{item.title}</h3>
                <p className="gallery-description">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-header">
          <h2 className="section-title">Client Testimonials</h2>
          <p className="section-description">Trusted by performance enthusiasts worldwide</p>
        </div>
        <div className="testimonials-grid">
          {mockTestimonials.map((testimonial) => (
            <Card key={testimonial.id} className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="star-icon" fill="currentColor" />
                  ))}
                </div>
                <p className="testimonial-comment">"{testimonial.comment}"</p>
                <div className="testimonial-author">
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-vehicle">{testimonial.vehicle}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2 className="contact-title">Ready to Transform Your Vehicle?</h2>
            <p className="contact-description">
              Get in touch with our team to discuss your performance goals. We'll create a custom tuning solution tailored to your needs.
            </p>
            <div className="contact-details">
              <div className="contact-detail-item">
                <h4 className="detail-label">Email</h4>
                <p className="detail-value">Torqueautohaus1@gmail.com</p>
              </div>
              <div className="contact-detail-item">
                <h4 className="detail-label">Hours</h4>
                <p className="detail-value">Mon-Fri: 9AM - 6PM<br />Sat: 10AM - 4PM</p>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
            />
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
            />
            <Input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleInputChange}
              className="form-input"
            />
            <Input
              type="text"
              name="vehicle"
              placeholder="Vehicle Year, Make, Model"
              value={formData.vehicle}
              onChange={handleInputChange}
              required
              className="form-input"
            />
            <Input
              type="text"
              name="rego"
              placeholder="Registration Number (Rego)"
              value={formData.rego}
              onChange={handleInputChange}
              className="form-input"
            />
            <Textarea
              name="message"
              placeholder="Tell us about your tuning goals..."
              value={formData.message}
              onChange={handleInputChange}
              required
              className="form-textarea"
              rows={5}
            />
            <Button type="submit" className="btn-primary submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Request Quote'}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <h3 className="footer-logo">TORQUE AUTOHAUS</h3>
            <p className="footer-tagline">Precision Engineering. Maximum Performance.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4 className="footer-heading">Services</h4>
              <a href="#performance-tuning" className="footer-link">Performance Tuning</a>
              <a href="#gallery" className="footer-link">Our Work</a>
              <a href="#contact" className="footer-link">Get Quote</a>
            </div>
            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <a href="#gallery" className="footer-link">Gallery</a>
              <a href="#testimonials" className="footer-link">Testimonials</a>
              <a href="#contact" className="footer-link">Contact</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">© 2024 Torque Autohaus. All rights reserved.</p>
        </div>
      </footer>

      {/* Floating WhatsApp Contact Button */}
      <a
        href="https://wa.me/6421471294?text=Hi%2C%20I%27d%20like%20a%20tuning%20quote"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-fab"
        aria-label="Chat with us on WhatsApp"
        data-testid="whatsapp-float-button"
      >
        <svg viewBox="0 0 32 32" className="whatsapp-fab-icon" aria-hidden="true">
          <path fill="currentColor" d="M16.001 3.2C8.935 3.2 3.2 8.935 3.2 16c0 2.26.6 4.463 1.74 6.404L3.2 28.8l6.56-1.72A12.74 12.74 0 0 0 16 28.8C23.066 28.8 28.8 23.065 28.8 16S23.066 3.2 16.001 3.2zm0 23.2c-1.96 0-3.884-.527-5.56-1.523l-.4-.237-3.893 1.02 1.04-3.793-.26-.39A10.55 10.55 0 0 1 5.4 16c0-5.845 4.756-10.6 10.6-10.6 5.845 0 10.6 4.755 10.6 10.6 0 5.844-4.755 10.6-10.6 10.6zm5.82-7.94c-.32-.16-1.89-.933-2.184-1.04-.293-.107-.507-.16-.72.16-.214.32-.826 1.04-1.013 1.253-.187.214-.373.24-.693.08-.32-.16-1.35-.498-2.572-1.587-.95-.847-1.592-1.893-1.778-2.213-.187-.32-.02-.493.14-.653.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.107-.214.053-.4-.027-.56-.08-.16-.72-1.733-.986-2.373-.26-.626-.524-.54-.72-.55l-.613-.011c-.213 0-.56.08-.853.4-.293.32-1.12 1.094-1.12 2.667 0 1.573 1.146 3.093 1.306 3.307.16.213 2.253 3.44 5.46 4.823.763.33 1.358.527 1.822.674.766.243 1.463.21 2.014.127.614-.092 1.89-.773 2.156-1.52.267-.746.267-1.386.187-1.52-.08-.133-.293-.213-.613-.373z"/>
        </svg>
      </a>
    </div>
  );
};

export default Landing;